// Find (⌘K or /, Console D, APS-322): anchored under the bar with the scope beside a mono input
// that expects the ids people paste (an issue's, a session's, `POST /v1/orders`). Empty, it shows
// what needs you and what you opened last; typed, it ranks by id and whole word, groups by kind
// with tabs (Tab cycles), previews the selected object on the right, and its foot says what ↵
// opens. It reads what the console already reads: the issues, a session by its id, the screens,
// the endpoints, the releases and the pages.
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useRouter, useSearch } from '@tanstack/react-router'
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { isApiError } from '../api/client'
import {
  endpointsQuery,
  issueQuery,
  issuesQuery,
  projectsQuery,
  screensQuery,
  sessionQuery,
  windowReleasesQuery,
} from '../api/queries'
import type { EndpointSummary, IssueSummary, ReleaseHealth, ScreenSummary } from '../api/types'
import { type MessageId, useI18n } from '../i18n'
import { duration } from '../lib/format'
import { glyphOf, issueTitle } from '../lib/issues'
import { latency, ttidAt } from '../lib/latency'
import { busiestApp, releasesNewestFirst } from '../lib/releases'
import { readTheme, saveTheme, type Theme } from '../lib/theme'
import { useProjectParams } from '../routes/params'
import { carried, type ScopeSearch, SETTINGS_SECTIONS } from '../routes/search'
import { sandboxFor } from '../sandbox'
import { Kind, STATUS_GLYPH, Status, type StatusKey } from './d'
import {
  arrange,
  type Entry,
  type EntryKind,
  type Group,
  highlight,
  nextTab,
  parse,
} from './find/model'
import { type Opened, recentIn, remember } from './find/recent'
import { PERF_PAGES } from './shell/Sidebar'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ISSUE_ID = /^[A-Za-z0-9._:-]{16,128}$/
const DAY = 86_400_000
/** An endpoint failing this often needs you. */
const FAILING = 0.05

/** Where an entry goes: a route of the console, or an action run in place. */
type Target =
  | { kind: 'route'; to: string; params?: Record<string, string>; search?: Record<string, unknown> }
  | { kind: 'action'; run: () => void }

type Item = Entry & {
  target: Target
  preview?: () => ReactNode
  /** An issue's status, for its glyph in the Issues list's colours. */
  status?: StatusKey | undefined
}

const GLYPH: Record<Exclude<EntryKind, 'issue'>, string> = {
  session: '◌',
  screen: '▭',
  endpoint: '⇄',
  release: '#',
  page: '→',
  setting: '⚙',
  action: '›',
}

const GROUP_LABEL: Record<Group | 'best', MessageId> = {
  best: 'find.group.best',
  issue: 'find.group.issue',
  session: 'find.group.session',
  perf: 'find.group.perf',
  release: 'find.group.release',
  page: 'find.group.page',
  action: 'find.group.action',
}

const GLYPH_STATUS = {
  new: 'new',
  ongoing: 'on',
  regressed: 'reg',
  resolved: 'res',
  ignored: 'mute',
} as const satisfies Record<ReturnType<typeof glyphOf>, StatusKey>

const OUTCOME: Record<string, MessageId> = {
  crashed: 'session.outcome.crashed',
  exited: 'session.outcome.exited',
  abnormal: 'session.outcome.abnormal',
  active: 'session.outcome.active',
}

const THEMES: Theme[] = ['system', 'light', 'dark']

/** The project's views Find lists, in the sidebar's order. */
const VIEWS = [
  { key: 'home', label: 'nav.home', to: '/o/$orgId/p/$projectId/home', keys: 'G H' },
  { key: 'issues', label: 'nav.issues', to: '/o/$orgId/p/$projectId/issues', keys: 'G I' },
  { key: 'flags', label: 'flags.title', to: '/o/$orgId/p/$projectId/flags' },
  { key: 'releases', label: 'nav.releases', to: '/o/$orgId/p/$projectId/releases', keys: 'G R' },
  {
    key: 'performance',
    label: 'nav.performance',
    to: '/o/$orgId/p/$projectId/performance',
    keys: 'G P',
  },
  { key: 'users', label: 'nav.people', to: '/o/$orgId/p/$projectId/users', keys: 'G U' },
  { key: 'sessions', label: 'nav.sessions', to: '/o/$orgId/p/$projectId/sessions', keys: 'G S' },
  { key: 'journeys', label: 'nav.journeys', to: '/o/$orgId/p/$projectId/journeys' },
  // Agents: in the sandbox, its token for the CLI and MCP (D105).
  { key: 'agents', label: 'nav.agents', to: '/o/$orgId/p/$projectId/agents', keys: 'G N' },
] as const satisfies readonly { key: string; label: MessageId; to: string; keys?: string }[]

/** run_query, dashboards, first run and the settings: none from the sandbox (D64). */
const OWN_VIEWS = [
  { key: 'explore', label: 'nav.explore', to: '/o/$orgId/p/$projectId/explore' },
  { key: 'dashboards', label: 'nav.dashboards', to: '/o/$orgId/p/$projectId/dashboards' },
  { key: 'setup', label: 'nav.start', to: '/o/$orgId/p/$projectId/setup' },
  {
    key: 'instrumentation',
    label: 'nav.instrumentation',
    to: '/o/$orgId/p/$projectId/instrumentation',
  },
  { key: 'signals', label: 'nav.signals', to: '/o/$orgId/p/$projectId/signals' },
] as const satisfies readonly { key: string; label: MessageId; to: string }[]

export function Find({
  onClose,
  onKeys,
  onRail,
}: {
  onClose: () => void
  /** Opens the keyboard shortcuts in Find's place. */
  onKeys: () => void
  onRail: () => void
}) {
  const i18n = useI18n()
  const { t, number, percent, relative } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const router = useRouter()
  const search = useSearch({ strict: false }) as ScopeSearch
  const ref = useModal(onClose)
  const listId = useId()
  const [raw, setRaw] = useState('')
  const [asked, setAsked] = useState<Group | 'all'>('all')
  const [active, setActive] = useState(0)
  const sandboxState = sandboxFor(projectId)
  const sandbox = !!sandboxState
  const keep: Record<string, unknown> = { ...carried(search) }
  const params = { orgId, projectId }
  const scope = { app_id: search.app, days: 7 }

  const projects = useQuery({ ...projectsQuery(orgId), enabled: !sandbox })
  const scopeLabel =
    sandboxState?.projectName ?? projects.data?.find((p) => p.id === projectId)?.name ?? ''
  // The project's busiest unresolved issues of the last 7 days, as the list opens on them: the
  // read API has no text search, so Find matches within them, and an id opens any issue.
  const issues = useQuery(
    issuesQuery(projectId, { ...scope, sort: 'users', state: 'unresolved', limit: 50 }),
  )
  const screens = useQuery(screensQuery(projectId, scope))
  const endpoints = useQuery(endpointsQuery(projectId, scope))
  // The shell's own 30-day list (the same key), so it is usually read already.
  const now = Math.floor(Date.now() / 60_000) * 60_000
  const releases = useQuery(windowReleasesQuery(projectId, 30, now))
  // "New" as the Issues list measures it: in the release the bar names, else the app's newest.
  const newRelease =
    search.release ??
    (releases.data
      ? releasesNewestFirst(releases.data, search.app ?? busiestApp(releases.data))[0]?.release
      : undefined)

  const q = parse(raw)
  const route = (to: string, p: Record<string, string> = {}, s: Record<string, unknown> = keep) =>
    ({ kind: 'route', to, params: { ...params, ...p }, search: s }) as const

  const releaseLine = (i: IssueSummary) => {
    const back = i.state?.regressed_release
    const resolved = i.state?.resolved_release
    if (back && resolved) return t('find.sub.issueBack', { resolved, back })
    if (back) return t('find.sub.issueRegressed', { back })
    return i.first_seen_release ? t('find.sub.issueSince', { release: i.first_seen_release }) : ''
  }
  const issueItem = (i: IssueSummary): Item => {
    const glyph = glyphOf(i, undefined, newRelease)
    return {
      key: `issue-${i.id}`,
      kind: 'issue',
      id: i.id,
      title: issueTitle(i),
      // Line 2, as the mock: the message (when the title is the type) and where it stands in
      // the releases, so two issues of one type can be told apart.
      sub: [i.type ? i.message : '', releaseLine(i)].filter(Boolean).join(' · ') || undefined,
      status: GLYPH_STATUS[glyph],
      meta: `${t('find.meta.sessions', { count: i.sessions })} · ${relative(i.last_seen)}`,
      mono: true,
      words: `${i.type} ${i.kind}`,
      target: route('/o/$orgId/p/$projectId/issues/$issueId', { issueId: i.id }),
      preview: () => (
        <>
          <p className="pv-k">
            <Status s={GLYPH_STATUS[glyph]} /> <Kind k={i.kind} />
          </p>
          <p className="pv-t mono" translate="no">
            {issueTitle(i)}
          </p>
          {i.message ? (
            <p className="pv-s" translate="no">
              {i.message}
            </p>
          ) : null}
          <dl className="pv-kv">
            <dt>{t('find.pv.sessions')}</dt>
            <dd>{number(i.sessions)}</dd>
            <dt>{t('find.pv.users')}</dt>
            <dd>{number(i.users)}</dd>
            <dt>{t('find.pv.since')}</dt>
            <dd>{i.first_seen_release ?? relative(i.first_seen)}</dd>
            <dt>{t('find.pv.seen')}</dt>
            <dd>{relative(i.last_seen)}</dd>
          </dl>
        </>
      ),
    }
  }
  const screenItem = (s: ScreenSummary): Item => {
    const ttfd = s.ttfd?.p90_ms
    return {
      key: `screen-${s.app_id}-${s.screen}`,
      kind: 'screen',
      id: s.screen,
      title: s.screen,
      sub: t('find.sub.screen', { count: s.loads }),
      meta: ttfd !== undefined ? `TTFD p90 ${duration(i18n, ttfd)}` : undefined,
      target: route(
        '/o/$orgId/p/$projectId/performance/screens/detail',
        {},
        {
          ...keep,
          screen: s.screen,
        },
      ),
      preview: () => (
        <>
          <p className="pv-k">
            <Kind k="screen" label={t('find.kind.screen')} />
          </p>
          <p className="pv-t" translate="no">
            {s.screen}
          </p>
          <dl className="pv-kv">
            <dt>{t('find.pv.loads')}</dt>
            <dd>{number(s.loads)}</dd>
            <dt>{t('find.pv.sessions')}</dt>
            <dd>{number(s.sessions)}</dd>
            <dt translate="no">TTID p90</dt>
            <dd title={ttidAt(s, 'p90_ms')?.approx ? t('perf.approx.title') : undefined}>
              {latency(i18n, ttidAt(s, 'p90_ms'))}
            </dd>
            <dt translate="no">TTFD p90</dt>
            <dd>{s.ttfd ? duration(i18n, s.ttfd.p90_ms) : '—'}</dd>
            <dt>{t('find.pv.hangs')}</dt>
            <dd>{number(s.hangs)}</dd>
          </dl>
        </>
      ),
    }
  }
  const endpointItem = (e: EndpointSummary): Item => {
    const name = `${e.method} ${e.template}`
    const failing = (e.error_rate ?? 0) >= FAILING
    return {
      key: `endpoint-${e.app_id}-${e.method}-${e.host}-${e.template}`,
      kind: 'endpoint',
      id: name,
      title: name,
      sub: e.host,
      meta:
        e.error_rate === null
          ? undefined
          : t('find.meta.failed', { rate: percent(e.error_rate, 1) }),
      mono: true,
      bad: failing,
      target: route(
        '/o/$orgId/p/$projectId/performance/network/endpoint',
        {},
        {
          ...keep,
          method: e.method,
          host: e.host,
          template: e.template,
        },
      ),
      preview: () => (
        <>
          <p className="pv-k">
            <Kind k="network" label={t('find.kind.endpoint')} />
          </p>
          <p className="pv-t mono" translate="no">
            {name}
          </p>
          <p className="pv-s mono" translate="no">
            {e.host}
          </p>
          <dl className="pv-kv">
            <dt>{t('find.pv.requests')}</dt>
            <dd>{number(e.requests)}</dd>
            <dt>{t('find.pv.failed')}</dt>
            <dd className={failing ? 'c-crash' : undefined}>
              {e.error_rate === null ? '—' : percent(e.error_rate, 1)}
            </dd>
            <dt>{t('find.pv.seen')}</dt>
            <dd>{relative(e.last_seen)}</dd>
          </dl>
        </>
      ),
    }
  }
  const releaseItem = (r: ReleaseHealth): Item => ({
    key: `release-${r.app_id}-${r.release}`,
    kind: 'release',
    id: r.release,
    title: r.release,
    sub: t('find.meta.sessions', { count: r.sessions }),
    meta: t('find.meta.crashFree', { rate: percent(r.crash_free_sessions_rate) }),
    mono: true,
    target: route(
      '/o/$orgId/p/$projectId/releases/$release',
      { release: r.release },
      { ...keep, release: undefined },
    ),
    preview: () => (
      <>
        <p className="pv-k">
          <Kind k="release" label={t('find.kind.release')} />
        </p>
        <p className="pv-t mono" translate="no">
          {r.release}
        </p>
        <dl className="pv-kv">
          <dt>{t('find.pv.crashFreeSessions')}</dt>
          <dd>{percent(r.crash_free_sessions_rate)}</dd>
          <dt>{t('find.pv.crashFreeUsers')}</dt>
          <dd>{r.crash_free_users_rate === null ? '—' : percent(r.crash_free_users_rate)}</dd>
          <dt>{t('find.pv.sessions')}</dt>
          <dd>{number(r.sessions)}</dd>
          <dt>{t('find.pv.since')}</dt>
          <dd>{relative(r.released_at)}</dd>
        </dl>
      </>
    ),
  })
  const page = (
    kind: 'page' | 'setting',
    key: string,
    title: string,
    target: Target,
    meta?: string,
  ): Item => ({
    key: `${kind}-${key}`,
    kind,
    id: title,
    title,
    sub: kind === 'setting' ? t('find.sub.settings') : undefined,
    meta,
    target,
  })
  const action = (key: string, title: string, run: () => void, sub?: string, meta?: string) =>
    ({
      key: `action-${key}`,
      kind: 'action',
      id: key,
      title,
      sub,
      meta,
      target: { kind: 'action', run },
    }) as Item

  const theme = readTheme()
  const nextTheme = THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length] ?? 'system'
  const items: Item[] = [
    ...(issues.data?.issues ?? []).map(issueItem),
    ...(screens.data ?? []).map(screenItem),
    ...(endpoints.data?.endpoints ?? []).map(endpointItem),
    ...(releases.data ?? []).filter((r) => !search.app || r.app_id === search.app).map(releaseItem),
    ...VIEWS.map((v) =>
      page('page', v.key, t(v.label), route(v.to), 'keys' in v ? v.keys : undefined),
    ),
    ...PERF_PAGES.map((p) =>
      page('page', p.key, `${t('nav.performance')} · ${t(p.label)}`, route(p.to)),
    ),
    ...(sandbox
      ? []
      : [
          ...OWN_VIEWS.map((v) => page('page', v.key, t(v.label), route(v.to))),
          ...SETTINGS_SECTIONS.map((s) =>
            page(
              'setting',
              s,
              t(`settings.tab.${s}`),
              route('/o/$orgId/p/$projectId/settings/$section', { section: s }, {}),
              s === 'general' ? 'G ,' : undefined,
            ),
          ),
        ]),
    action('keys', t('keys.title'), onKeys, undefined, '?'),
    action('rail', t('keys.rail'), onRail, undefined, '['),
    action(
      'theme',
      t('find.action.theme', {
        theme: t(`user.theme.${nextTheme === 'system' ? 'systemShort' : nextTheme}`),
      }),
      () => saveTheme(nextTheme),
    ),
  ]

  // An id pasted in that the lists above do not have: a session's (a UUID), or an issue's.
  const text = q.text
  const direct: Item[] = UUID.test(text)
    ? [
        {
          key: `session-${text.toLowerCase()}`,
          kind: 'session',
          id: text.toLowerCase(),
          title: text.toLowerCase(),
          sub: t('find.sub.byId'),
          mono: true,
          target: route('/o/$orgId/p/$projectId/sessions/$sessionId', {
            sessionId: text.toLowerCase(),
          }),
          preview: () => <SessionPreview projectId={projectId} sessionId={text.toLowerCase()} />,
        },
      ]
    : ISSUE_ID.test(text) && !items.some((i) => i.kind === 'issue' && i.id === text)
      ? [
          {
            key: `issue-${text}`,
            kind: 'issue',
            id: text,
            title: text,
            sub: t('find.sub.byId'),
            mono: true,
            target: route('/o/$orgId/p/$projectId/issues/$issueId', { issueId: text }),
            preview: () => <IssuePreview projectId={projectId} issueId={text} />,
          },
        ]
      : []

  // Empty: what needs you (issues that came back or are new, an endpoint that fails), then what
  // Find opened last; never a list of every page.
  const recent = recentIn(projectId)
  const dayAgo = Date.now() - DAY
  const needs: Item[] = raw.trim()
    ? []
    : [
        ...(issues.data?.issues ?? [])
          .filter((i) => i.state?.regressed_release || Date.parse(i.first_seen) >= dayAgo)
          .sort(
            (a, b) => Number(!!b.state?.regressed_release) - Number(!!a.state?.regressed_release),
          )
          .slice(0, 2)
          .map(issueItem),
        ...[...(endpoints.data?.endpoints ?? [])]
          .filter((e) => (e.error_rate ?? 0) >= FAILING)
          .sort((a, b) => (b.error_rate ?? 0) - (a.error_rate ?? 0))
          .slice(0, 1)
          .map(endpointItem),
      ]
  const opened: Item[] = raw.trim()
    ? []
    : recent
        .filter((o) => !needs.some((n) => n.key === o.key))
        .slice(0, 5)
        .map((o) => items.find((i) => i.key === o.key) ?? openedItem(o))

  const arranged = raw.trim() ? arrange([...direct, ...items], q, asked) : undefined
  const sections: { group: Group | 'best' | 'needs' | 'recent'; entries: Item[] }[] = arranged
    ? (arranged.sections as { group: Group | 'best'; entries: Item[] }[])
    : [
        ...(needs.length ? [{ group: 'needs' as const, entries: needs }] : []),
        ...(opened.length ? [{ group: 'recent' as const, entries: opened }] : []),
      ]
  const shown = sections.flatMap((s) => s.entries)
  const current = Math.min(active, Math.max(0, shown.length - 1))
  const selected = shown[current]
  const optionId = (i: number) => `${listId}-${i}`
  const loading = issues.isPending || screens.isPending || endpoints.isPending

  function openedItem(o: Opened): Item {
    return {
      ...o,
      target: { kind: 'route', to: '', search: { href: o.href } },
    }
  }
  const hrefOf = (item: Item) => {
    if (item.target.kind === 'action') return ''
    if (!item.target.to) return String(item.target.search?.href ?? '')
    return router.buildLocation({
      to: item.target.to,
      params: item.target.params,
      search: item.target.search,
    } as never).href
  }
  const shortHref = (href: string) => href.replace(`/o/${orgId}/p/${projectId}`, '') || '/'

  const pick = (item: Item | undefined) => {
    if (!item) return
    ref.current?.close()
    if (item.target.kind === 'action') {
      item.target.run()
      return
    }
    const href = hrefOf(item)
    if (item.kind !== 'page' && item.kind !== 'setting')
      remember(projectId, {
        key: item.key,
        kind: item.kind,
        id: item.id,
        title: item.title,
        sub: item.sub,
        mono: item.mono,
        href,
      })
    if (!item.target.to) navigate({ href })
    else
      navigate({
        to: item.target.to,
        params: item.target.params,
        search: item.target.search,
      } as never)
  }

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && arranged) {
      e.preventDefault()
      setAsked(nextTab(arranged, e.shiftKey))
      setActive(0)
      return
    }
    if (shown.length === 0) return
    const move = (to: number) => {
      e.preventDefault()
      setActive(to)
      document.getElementById(optionId(to))?.scrollIntoView?.({ block: 'nearest' })
    }
    if (e.key === 'ArrowDown') move((current + 1) % shown.length)
    else if (e.key === 'ArrowUp') move((current - 1 + shown.length) % shown.length)
    else if (e.key === 'Home') move(0)
    else if (e.key === 'End') move(shown.length - 1)
    else if (e.key === 'Enter') {
      e.preventDefault()
      pick(selected)
    }
  }

  let index = -1
  return (
    <dialog ref={ref} className="pal" aria-label={t('find.label')}>
      <div className="pal-in">
        <span className="pal-scope" translate="no">
          {scopeLabel}
        </span>
        <input
          type="text"
          role="combobox"
          aria-expanded={shown.length > 0}
          aria-controls={shown.length > 0 ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={shown.length > 0 ? optionId(current) : undefined}
          aria-label={t('find.label')}
          placeholder={t('find.placeholder')}
          autoComplete="off"
          spellCheck={false}
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value)
            setActive(0)
            if (!e.target.value.trim()) setAsked('all')
          }}
          onKeyDown={onKeyDown}
        />
        <kbd translate="no">esc</kbd>
      </div>
      {arranged && arranged.tabs.length > 1 ? (
        <div className="pal-tabs" role="tablist" aria-label={t('find.kinds')}>
          {arranged.tabs.map((tab) => (
            <button
              key={tab.group}
              type="button"
              role="tab"
              aria-selected={tab.group === arranged.tab}
              tabIndex={-1}
              onClick={() => {
                setAsked(tab.group)
                setActive(0)
              }}
            >
              {tab.group === 'all' ? t('find.all') : t(GROUP_LABEL[tab.group])}
              <span>{number(tab.count)}</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="pal-body">
        <div className="pal-list" aria-busy={shown.length === 0 && !raw.trim() && loading}>
          {/* A listbox only with options in it (axe: aria-required-children); the empty notes
              sit beside it. */}
          {shown.length > 0 ? (
            <div id={listId} role="listbox" aria-label={t('find.results')}>
              {sections.map((s) => (
                // biome-ignore lint/a11y/useSemanticElements: an option group inside a listbox, which a fieldset cannot be
                <div key={s.group} role="group" aria-label={groupLabel(s.group)}>
                  <p className="pal-g" aria-hidden="true">
                    {groupLabel(s.group)}
                  </p>
                  {s.entries.map((item) => {
                    index += 1
                    const i = index
                    return (
                      // The input keeps focus and moves through the options (aria-activedescendant):
                      // an option is reached by the arrows, not by Tab, and a click picks it.
                      // biome-ignore lint/a11y/useKeyWithClickEvents: the input handles the arrows and Enter
                      <div
                        key={item.key}
                        id={optionId(i)}
                        className="pal-i"
                        role="option"
                        tabIndex={-1}
                        aria-selected={i === current}
                        onClick={() => pick(item)}
                        onMouseMove={() => i !== current && setActive(i)}
                      >
                        <span className="pal-kd" aria-hidden="true">
                          {item.kind !== 'issue' ? (
                            GLYPH[item.kind]
                          ) : item.status ? (
                            <span className={`st ${item.status}`}>{STATUS_GLYPH[item.status]}</span>
                          ) : (
                            '●'
                          )}
                        </span>
                        <span className="pal-tt">
                          <span
                            className={item.mono ? 'pal-t mono' : 'pal-t'}
                            translate={item.mono ? 'no' : undefined}
                          >
                            <Marked text={item.title} terms={q.terms} />
                          </span>
                          {item.sub ? (
                            <span className="pal-s">
                              <Marked text={item.sub} terms={q.terms} />
                            </span>
                          ) : null}
                        </span>
                        <span className={item.bad ? 'k2 c-crash' : 'k2'}>{item.meta ?? ''}</span>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          ) : null}
          {shown.length === 0 && raw.trim() ? (
            <div className="pal-none">
              <b>{t('find.none', { query: raw.trim(), scope: scopeLabel })}</b>
              <span>{t('find.noneHint')}</span>
            </div>
          ) : null}
          {shown.length === 0 && !raw.trim() ? (
            <p className="pal-none">{loading ? t('state.loading') : t('find.emptyHint')}</p>
          ) : null}
        </div>
        <aside className="pal-prev" aria-live="polite" aria-label={t('find.preview')}>
          {selected ? (selected.preview?.() ?? <PlainPreview item={selected} />) : null}
        </aside>
      </div>
      {selected ? (
        <p className="pal-foot">
          <span>
            <kbd>↵</kbd>{' '}
            {selected.target.kind === 'action' ? (
              selected.title.toLowerCase()
            ) : (
              <code translate="no">{shortHref(hrefOf(selected))}</code>
            )}
          </span>
          <span className="r">
            {raw.trim() ? (
              <>
                <kbd translate="no">tab</kbd> {t('find.foot.next')}
              </>
            ) : (
              <>
                <code translate="no">/</code> <code translate="no">&gt;</code>{' '}
                {t('find.foot.narrow')}
              </>
            )}
          </span>
        </p>
      ) : null}
    </dialog>
  )

  function groupLabel(g: Group | 'best' | 'needs' | 'recent') {
    if (g === 'needs') return t('find.group.needs')
    if (g === 'recent') return t('find.group.recent')
    return t(GROUP_LABEL[g])
  }
}

function Marked({ text, terms }: { text: string; terms: readonly string[] }) {
  return (
    <>
      {highlight(text, terms).map((p, i) =>
        // biome-ignore lint/suspicious/noArrayIndexKey: parts of one string, in order
        p.on ? <mark key={i}>{p.t}</mark> : <span key={i}>{p.t}</span>,
      )}
    </>
  )
}

const KIND_WORD: Record<EntryKind, MessageId> = {
  issue: 'find.kind.issue',
  session: 'find.kind.session',
  screen: 'find.kind.screen',
  endpoint: 'find.kind.endpoint',
  release: 'find.kind.release',
  page: 'find.kind.page',
  setting: 'find.kind.setting',
  action: 'find.kind.action',
}

function PlainPreview({ item }: { item: Item }) {
  const { t } = useI18n()
  return (
    <>
      <p className="pv-k">
        <Kind k={item.kind} label={t(KIND_WORD[item.kind])} />
      </p>
      <p className={item.mono ? 'pv-t mono' : 'pv-t'} translate={item.mono ? 'no' : undefined}>
        {item.title}
      </p>
      {item.sub ? <p className="pv-s">{item.sub}</p> : null}
    </>
  )
}

/** A session pasted by its id: read when selected, to say what it was before opening it. */
function SessionPreview({ projectId, sessionId }: { projectId: string; sessionId: string }) {
  const i18n = useI18n()
  const { t, number, relative } = i18n
  const session = useQuery(sessionQuery(projectId, sessionId))
  const s = session.data?.summary
  return (
    <>
      <p className="pv-k">
        <Kind k="session" label={t('find.kind.session')} />
      </p>
      <p className="pv-t mono" translate="no">
        {sessionId}
      </p>
      {session.isPending ? (
        <p className="pv-n">{t('state.loading')}</p>
      ) : !s ? (
        <p className="pv-n">
          {isApiError(session.error, 404) ? t('find.pv.notFound') : t('state.failed')}
        </p>
      ) : (
        <dl className="pv-kv">
          <dt>{t('find.pv.outcome')}</dt>
          <dd>{OUTCOME[s.outcome] ? t(OUTCOME[s.outcome] as MessageId) : s.outcome}</dd>
          <dt>{t('find.pv.release')}</dt>
          <dd translate="no">{s.release}</dd>
          <dt>{t('find.pv.length')}</dt>
          <dd>{duration(i18n, Date.parse(s.last_seen_at) - Date.parse(s.started_at))}</dd>
          <dt>{t('find.pv.started')}</dt>
          <dd>{relative(s.started_at)}</dd>
          <dt>{t('find.pv.errors')}</dt>
          <dd>{number(s.error_count)}</dd>
        </dl>
      )}
    </>
  )
}

/** An issue pasted by its id, not among the busiest: read when selected. */
function IssuePreview({ projectId, issueId }: { projectId: string; issueId: string }) {
  const { t, number, relative } = useI18n()
  const issue = useQuery({ ...issueQuery(projectId, issueId), retry: false })
  const i = issue.data?.issue
  return (
    <>
      <p className="pv-k">
        {i ? <Kind k={i.kind} /> : <Kind k="issue" label={t('find.kind.issue')} />}
      </p>
      <p className="pv-t mono" translate="no">
        {i ? issueTitle(i) : issueId}
      </p>
      {issue.isPending ? (
        <p className="pv-n">{t('state.loading')}</p>
      ) : !i ? (
        <p className="pv-n">
          {isApiError(issue.error, 404) ? t('find.pv.notFound') : t('state.failed')}
        </p>
      ) : (
        <dl className="pv-kv">
          <dt>{t('find.pv.sessions')}</dt>
          <dd>{number(i.sessions)}</dd>
          <dt>{t('find.pv.users')}</dt>
          <dd>{number(i.users)}</dd>
          <dt>{t('find.pv.seen')}</dt>
          <dd>{relative(i.last_seen)}</dd>
        </dl>
      )}
    </>
  )
}

/** A modal dialog while it is mounted: open on mount, `onClose` when the browser closes it. */
export function useModal(onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null)
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    // The browser gives focus back on close, but not to a dialog taken out of the page (another
    // key replaced it): this does it in both cases.
    const before = document.activeElement
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal()
    } else dialog.setAttribute('open', '')
    const onDialogClose = () => close.current()
    dialog.addEventListener('close', onDialogClose)
    return () => {
      dialog.removeEventListener('close', onDialogClose)
      if (dialog.open && typeof dialog.close === 'function') dialog.close()
      if (before instanceof HTMLElement && before.isConnected) before.focus()
    }
  }, [])
  return ref
}
